import { Box, Button, CircularProgress, Dialog, DialogContent, Grid, Paper, Stack, Typography, useTheme } from '@mui/material';
import {
    GoogleAuthProvider,
    unlink,
    updatePassword
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useUserService } from '../../services/user.service';
import type { NotificationSettings } from '../../types/user';
import PageLayout from '../common/PageLayout';
import ChangeEmailDialog from './dialogs/ChangeEmailDialog';
import ChangePasswordDialog from './dialogs/ChangePasswordDialog';
import DeleteAccountDialog from './dialogs/DeleteAccountDialog';
import LogoutPromptDialog from './dialogs/LogoutPromptDialog';
import UnlinkGoogleDialog from './dialogs/UnlinkGoogleDialog';
import { useSettingsQueries } from './hooks/useSettingsQueries';
import LanguageSettings from './sections/LanguageSettings';
import PlanSection from './sections/PlanSection';
import ProfileSection from './sections/ProfileSection';
import TaxSection from './sections/TaxSection';
import InvoiceHistorySection from './sections/InvoiceHistorySection';

const SettingsPage = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const defaultNotificationSettings: NotificationSettings = {
        productUpdates: true,
    };
    const { showNotification } = useNotification();
    const userService = useUserService();

    const { token, currentUser, currentCtUser, logout, sendVerificationEmail, loginWithGoogle } = useAuth();
    const navigate = useNavigate();

    // Dialog visibility states
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [showUnlinkGoogleDialog, setShowUnlinkGoogleDialog] = useState(false);
    const [showLogoutPrompt, setShowLogoutPrompt] = useState(false);
    const [showDeletionProgressDialog, setShowDeletionProgressDialog] = useState(false);

    // We'll use the notification context instead of local state for messages

    // Form input states
    const [newEmail, setNewEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [deletePassword, setDeletePassword] = useState('');

    const isGoogleUser =
        (currentUser?.providerData &&
            currentUser.providerData.length > 0 &&
            currentUser.providerData[0]?.providerId === 'google.com') ||
        false;

    const onNotificationSettingsError = (error: any) => {
        console.error('Error loading notifications:', error);
        if (error.message?.includes('auth/quota-exceeded')) {
            showNotification(t('settings.errors.tooManyRequests'), 'error');
        } else {
            showNotification(t('settings.errors.loadNotificationSettingsFailed'), 'error');
        }
    };

    const {
        // Queries
        notificationSettings,
        isNotificationsLoading,

        // Mutations
        updateNotification,
        isUpdatingNotification,
        deleteAccount,
        isDeleting,
        updateEmail,
        isEmailUpdating,
        updatePassword: updateUserPassword,
        isPasswordUpdating,
    } = useSettingsQueries(defaultNotificationSettings, onNotificationSettingsError, isGoogleUser);

    const handleNotificationChange = (setting: keyof NotificationSettings) => {
        if (!currentUser) return;
        updateNotification({
            setting,
            onSuccess: () => showNotification(t('settings.success.notificationSettingsUpdated'), 'success'),
            onError: () => showNotification(t('settings.errors.updateNotificationSettingsFailed'), 'error'),
        });
    };

    // Dialog close handlers
    const handleCloseEmailDialog = () => {
        setShowEmailDialog(false);
        setCurrentPassword('');
        setNewEmail('');
    };

    const handleClosePasswordDialog = () => {
        setShowPasswordDialog(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleCloseUnlinkGoogleDialog = () => {
        setShowUnlinkGoogleDialog(false);
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleCloseDeleteDialog = () => {
        setShowDeleteDialog(false);
        setDeleteConfirmation('');
        setDeletePassword('');
    };

    const handleDeleteAccount = () => {
        if (!currentUser) return;
        deleteAccount({
            isGoogleUser,
            deletePassword,
            onSuccess: () => {
                handleCloseDeleteDialog();
            },
            onError: (err) => showNotification(err.message || 'Failed to delete account', 'error'),
        });
    };

    useEffect(() => {
        const finishDeleteAccount = async () => {
            if (!currentUser) return;
            const url = new URL(window.location.href);
            const deleteAccount = url.searchParams.get("deleteAccount");
            if (deleteAccount) {
                setShowDeletionProgressDialog(true);
                try {
                    await userService.deleteAccount();
                    await currentUser.delete();
                    navigate('/login');
                } catch(e) {
                    console.error('Error deleting account:', e);
                    showNotification(t('settings.errors.deleteAccountFailed'), 'error');
                } finally {
                    setShowDeletionProgressDialog(false);
                }
            }
        };
    
        finishDeleteAccount();
    }, []);

    const handleEmailUpdate = () => {
        if (!currentUser) return;
        updateEmail({
            newEmail,
            currentPassword,
            onSuccess: () => {
                handleCloseEmailDialog();
                showNotification(t('settings.success.verificationEmailSent'), 'success');
                setCurrentPassword('');
            },
            onError: (err) => {
                let message = t('settings.errors.sendVerificationEmailFailed');
                if (err.code === 'auth/requires-recent-login') message = t('settings.errors.requiresRecentLogin');
                else if (err.code === 'auth/invalid-email') message = t('settings.errors.invalidEmail');
                else if (err.code === 'auth/invalid-credential') message = t('settings.errors.invalidPassword');
                else if (
                    err.response &&
                    err.response.data &&
                    err.response.data.error === 'INTERNAL ASSERT FAILED: Unable to create the email action link'
                )
                    message = t('settings.errors.emailExists');
                showNotification(message, 'error');
            },
        });
    };

    const handlePasswordUpdate = () => {
        if (!currentUser) return;
        updateUserPassword({
            currentPassword,
            newPassword,
            confirmPassword,
            onSuccess: () => {
                handleClosePasswordDialog();
                showNotification(t('settings.success.passwordUpdated'), 'success');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            },
            onError: (err) => showNotification(err.message || t('settings.errors.updatePasswordFailed'), 'error'),
        });
    };

    const handleUnlinkGoogle = () => {
        if (!currentUser) return;
        if (newPassword !== confirmPassword) {
            showNotification(t('settings.errors.updatePasswordFailed'), 'error');
            return;
        } 
        loginWithGoogle(`/settings?unlinkGoogle=true&newPassword=${newPassword}`);
    };

    useEffect(() => {
        const finishUnlinkGoogle = async () => {
            if (!currentUser) return;
            const url = new URL(window.location.href);
            const unlinkGoogle = url.searchParams.get("unlinkGoogle");
            const newPassword = url.searchParams.get("newPassword");
            if (unlinkGoogle && newPassword) {
                await updatePassword(currentUser, newPassword || '')
                await unlink(currentUser, GoogleAuthProvider.PROVIDER_ID);
                showNotification(t('settings.success.googleUnlinked'), 'success');
            }
        };
    
        finishUnlinkGoogle();
    }, []);

    const handleLogoutAndVerify = async () => {
        setShowLogoutPrompt(false);
        await logout();
        navigate('/login');
    };

    const handleResendVerification = async () => {
        try {
            await sendVerificationEmail();
            showNotification('Verification email sent. Please check your inbox.', 'success');
        } catch (error) {
            console.error('Error sending verification email:', error);
            showNotification('Failed to send verification email. Please try again later.', 'error');
        }
    };

    const isLoading =
        isNotificationsLoading ||
        isUpdatingNotification ||
        isDeleting ||
        isEmailUpdating ||
        isPasswordUpdating;

    return (
        <PageLayout title={t('settings.title')}>
            <Box
                sx={{
                    bgcolor: theme.palette.background.default,
                    width: '100%',
                    minHeight: '100vh',
                    pt: 2,
                }}
            >
                <Grid container spacing={3}>
                    {/* Left Column */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            <ProfileSection
                                email={currentUser?.email || ''}
                                isGoogleUser={isGoogleUser}
                                notificationSettings={notificationSettings}
                                isLoading={isNotificationsLoading || isUpdatingNotification}
                                supportId={currentCtUser?.ctId?.toString()}
                                emailVerified={currentUser?.emailVerified || false}
                                onChangeEmail={() => setShowEmailDialog(true)}
                                onChangePassword={() => setShowPasswordDialog(true)}
                                onUnlinkGoogle={() => setShowUnlinkGoogleDialog(true)}
                                onSettingChange={handleNotificationChange}
                                onResendVerification={handleResendVerification}
                            />

                            <PlanSection />

                            <Box>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 3,
                                        bgcolor: 'error.lighter',
                                        borderColor: 'divider',
                                        borderWidth: '1px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            color: 'error.dark',
                                            fontWeight: 'bold',
                                            fontSize: '1.25rem',
                                            mb: 2,
                                        }}
                                    >
                                        {t('settings.account.buttons.deleteAccount')}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mb: 2,
                                            color: 'text.secondary',
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {t('settings.dialogs.deleteAccount.description')}
                                    </Typography>

                                    <Stack spacing={1.5}>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() => setShowDeleteDialog(true)}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                height: '40px',
                                            }}
                                        >
                                            {t('settings.dialogs.deleteAccount.buttons.delete')}
                                        </Button>
                                    </Stack>
                                </Paper>
                            </Box>

                            { import.meta.env.MODE === 'development' && (
                            <LanguageSettings />
                        )}
                        </Stack>
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            <TaxSection />
                            <InvoiceHistorySection />
                        </Stack>
                    </Grid>
                </Grid>

                {/* Dialogs */}
                <DeleteAccountDialog
                    open={showDeleteDialog}
                    isGoogleUser={isGoogleUser}
                    isLoading={isLoading}
                    deleteConfirmation={deleteConfirmation}
                    deletePassword={deletePassword}
                    onClose={handleCloseDeleteDialog}
                    onConfirm={handleDeleteAccount}
                    onDeleteConfirmationChange={setDeleteConfirmation}
                    onDeletePasswordChange={setDeletePassword}
                />

                <ChangePasswordDialog
                    open={showPasswordDialog}
                    isLoading={isLoading}
                    currentPassword={currentPassword}
                    newPassword={newPassword}
                    confirmPassword={confirmPassword}
                    onClose={handleClosePasswordDialog}
                    onConfirm={handlePasswordUpdate}
                    onCurrentPasswordChange={setCurrentPassword}
                    onNewPasswordChange={setNewPassword}
                    onConfirmPasswordChange={setConfirmPassword}
                />

                <ChangeEmailDialog
                    open={showEmailDialog}
                    isGoogleUser={isGoogleUser}
                    isLoading={isLoading}
                    newEmail={newEmail}
                    currentPassword={currentPassword}
                    onClose={handleCloseEmailDialog}
                    onConfirm={handleEmailUpdate}
                    onNewEmailChange={setNewEmail}
                    onCurrentPasswordChange={setCurrentPassword}
                />

                <UnlinkGoogleDialog
                    open={showUnlinkGoogleDialog}
                    isLoading={isLoading}
                    error="" // Empty string as we're using NotificationContext
                    newPassword={newPassword}
                    confirmPassword={confirmPassword}
                    onClose={handleCloseUnlinkGoogleDialog}
                    onConfirm={handleUnlinkGoogle}
                    onNewPasswordChange={setNewPassword}
                    onConfirmPasswordChange={setConfirmPassword}
                />

                <LogoutPromptDialog
                    open={showLogoutPrompt}
                    onClose={() => setShowLogoutPrompt(false)}
                    onConfirm={handleLogoutAndVerify}
                />

                {/* Deletion Progress Dialog */}
                <Dialog
                    open={showDeletionProgressDialog}
                    disableEscapeKeyDown
                    aria-labelledby="deletion-progress-dialog-title"
                >
                    <DialogContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        py: 4,
                        px: 6 
                    }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography id="deletion-progress-dialog-title" variant="h6">
                            {t('settings.dialogs.deleteAccount.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                            {t('settings.dialogs.deleteAccount.deleting')}
                        </Typography>
                    </DialogContent>
                </Dialog>

                {/* Using NotificationContext instead of Snackbar */}
            </Box>
        </PageLayout>
    );
};

export default SettingsPage;
